import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Upload, X, Save, Building2, Key, CheckCircle, AlertCircle, Calendar, Shield, Pen, HardDrive, Receipt, Bell, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useLicense } from '../contexts/LicenseContext';
import { useAuth } from '../contexts/AuthContext';
import LicenseModal from '../components/LicenseModal';
import FeedbackModal from '../components/FeedbackModal';
import SignaturePad from '../components/SignaturePad';
import StoragePage from './Storage';
import { IconTile, TINT } from '../components/ui/IconTile';

/**
 * Unified section header for the Settings page — mirrors the look of
 * the Storage page cards: icon tile on the left, title on top, subtitle
 * underneath, everything left-aligned.
 */
const SectionHeader = ({ icon, tint, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
    <IconTile icon={icon} tint={tint} size={38} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>{title}</h2>
      {subtitle && (
        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>{subtitle}</p>
      )}
    </div>
  </div>
);

const Settings = () => {
  const { user } = useAuth();
  // ?tab=storage deep-links the Storage sub-view so the legacy /storage route + sidebar shortcut still work.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'storage' ? 'storage' : 'settings';
  const [activeTab, setActiveTab] = useState(initialTab);
  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'storage') setSearchParams({ tab: 'storage' }, { replace: true });
    else setSearchParams({}, { replace: true });
  };

  const { 
    hasLicense, 
    isValid, 
    isTrialMode, 
    trialDaysRemaining, 
    licenseDaysRemaining,
    expiryDate,
    licenseKey,
    removeLicense
  } = useLicense();
  
  const [companyInfo, setCompanyInfo] = useState({
    name: 'Maidly Business Manager',
    address: '',
    phone: '',
    email: 'deevelopeer8@gmail.com',
    website: '',
    logo: '',
    supportEmail: 'deevelopeer8@gmail.com'
  });
  const [taxRate, setTaxRate] = useState(8);
  const [reminders, setReminders] = useState({
    enabled: true,
    daysBefore: 3,
    includeOverdue: true,
    customMessage: '',
  });
  const [senderSmtp, setSenderSmtp] = useState({
    enabled: false,
    email: '',
    password: '',
    server: 'smtp.gmail.com',
    port: 587,
  });
  const [senderTestStatus, setSenderTestStatus] = useState({ state: 'idle', message: '' });
  const [showSenderAdvanced, setShowSenderAdvanced] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [companySignature, setCompanySignature] = useState('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  // Security preferences — auto-logout is enabled by default, but users
  // running live GPS Fleet tracking can disable it so idle map viewing
  // doesn't kick them out mid-shift.
  const [security, setSecurity] = useState({ autoLogout: true });

  useEffect(() => {
    loadSettings();
    // Re-read when another device's company-info update arrives via sync.
    const onSettingsSynced = () => loadSettings();
    window.addEventListener('maidly:settings-updated', onSettingsSynced);
    return () => window.removeEventListener('maidly:settings-updated', onSettingsSynced);
  }, []);

  const loadSettings = () => {
    const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
    if (settings.companyInfo) {
      setCompanyInfo(settings.companyInfo);
      setLogoPreview(settings.companyInfo.logo || '');
    }
    if (settings.taxRate !== undefined) {
      setTaxRate(settings.taxRate);
    }
    if (settings.reminders) {
      setReminders((prev) => ({ ...prev, ...settings.reminders }));
    }
    if (settings.senderSmtp) {
      setSenderSmtp((prev) => ({ ...prev, ...settings.senderSmtp }));
    }
    if (settings.companySignature) {
      setCompanySignature(settings.companySignature);
    }
    if (settings.security && typeof settings.security.autoLogout === 'boolean') {
      setSecurity({ autoLogout: settings.security.autoLogout });
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setLogoPreview(base64String);
      setCompanyInfo({ ...companyInfo, logo: base64String });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview('');
    setCompanyInfo({ ...companyInfo, logo: '' });
    toast.success('Logo removed');
  };

  const saveSettings = () => {
    const settings = {
      companyInfo,
      taxRate,
      salesTaxRate: taxRate,
      reminders,
      senderSmtp,
      companySignature,
      security,
    };
    localStorage.setItem('maidly_settings', JSON.stringify(settings));
    // Broadcast so open modals / other tabs (Invoices, Estimates, Recurring)
    // pick up the fresh tax rate + company info without a page reload.
    try {
      window.dispatchEvent(new CustomEvent('maidly:settings-updated', {
        detail: { source: 'settings-tab' },
      }));
    } catch (err) {
      console.warn('[settings] settings-updated broadcast failed', err);
    }
    toast.success('Settings saved successfully');
  };

  const testSenderCredentials = async () => {
    if (!senderSmtp.email || !senderSmtp.password) {
      setSenderTestStatus({ state: 'error', message: 'Enter your email and app password first.' });
      return;
    }
    setSenderTestStatus({ state: 'testing', message: 'Verifying credentials...' });
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/invoices/test-sender`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: senderSmtp.email,
          password: senderSmtp.password,
          server: senderSmtp.server || 'smtp.gmail.com',
          port: Number(senderSmtp.port) || 587,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setSenderTestStatus({ state: 'ok', message: `Verified! Reminders will send from ${senderSmtp.email}.` });
        toast.success('Sender email verified');
      } else {
        const msg = typeof body.detail === 'string' ? body.detail : 'Verification failed. Check your email + app password.';
        setSenderTestStatus({ state: 'error', message: msg });
        toast.error(msg);
      }
    } catch (e) {
      const msg = `Network error: ${e.message}`;
      setSenderTestStatus({ state: 'error', message: msg });
      toast.error(msg);
    }
  };

  const handleSaveSignature = (signatureData) => {
    setCompanySignature(signatureData);
    setShowSignaturePad(false);
    toast.success('Signature saved! Remember to save settings.');
  };

  const removeSignature = () => {
    setCompanySignature('');
    toast.success('Signature removed');
  };

  return (
    <div data-testid="settings-page">
      {/* Tabs: Settings ↔ Storage — same visual language as Time Clock / Employees / Inspections tabs */}
      <div
        role="tablist"
        aria-label="Settings views"
        style={{
          display: 'inline-flex', gap: 6, padding: 5, marginBottom: 16,
          background: '#f1f5f9', borderRadius: 12, border: '1px solid #e2e8f0',
          boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.04)',
        }}
      >
        {[
          { id: 'settings', label: 'Settings', icon: Building2, tint: TINT.settings },
          { id: 'storage', label: 'Storage', icon: HardDrive, tint: TINT.storage },
        ].map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              data-testid={`settings-tab-${t.id}`}
              onClick={() => switchTab(t.id)}
              style={{
                all: 'unset', cursor: 'pointer',
                padding: '8px 18px 8px 8px', borderRadius: 9,
                fontSize: 13, fontWeight: 700, letterSpacing: 0.1,
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: isActive ? '#0073ea' : 'transparent',
                color: isActive ? '#ffffff' : '#475569',
                boxShadow: isActive ? '0 3px 10px rgba(0,115,234,0.28)' : 'none',
                transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(15,23,42,0.05)'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <IconTile icon={Icon} tint={isActive ? 'rgba(255,255,255,0.22)' : t.tint} size={28} iconSize={15} />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'storage' ? (
        <StoragePage />
      ) : (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.75rem', margin: 0 }}>
            <IconTile icon={Building2} tint={TINT.settings} size={34} />
            Settings
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: '0.35rem 0 0 0' }}>
            Company profile, tax defaults, email preferences, and license
          </p>
        </div>
        <button
          onClick={saveSettings}
          data-testid="save-settings-button"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            padding: '0.55rem 1rem 0.55rem 0.55rem',
            background: TINT.settings, color: '#fff',
            border: 'none', borderRadius: 12,
            fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
            boxShadow: `0 6px 16px ${TINT.settings}55`,
            transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            letterSpacing: 0.1,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#475569';
            e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.settings}88`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = TINT.settings;
            e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.settings}55`;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <IconTile icon={Save} tint="rgba(255,255,255,0.22)" size={30} />
          Save Settings
        </button>
      </div>

      {/* Company Information */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <SectionHeader
          icon={Building2}
          tint={TINT.settings}
          title="Company Information"
          subtitle="Shown on invoices, quotes, and reminder emails"
        />

        {/* Logo Upload */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Company Logo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            {logoPreview ? (
              <div style={{ position: 'relative' }}>
                <img
                  src={logoPreview}
                  alt="Company Logo"
                  style={{
                    width: '150px',
                    height: '150px',
                    objectFit: 'contain',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    background: '#fff'
                  }}
                />
                <button
                  onClick={removeLogo}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: '#e44258',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                  title="Remove logo"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div style={{
                width: '150px',
                height: '150px',
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f9fafb'
              }}>
                <Upload size={32} color="#9ca3af" />
              </div>
            )}
            
            <div>
              <label htmlFor="logo-upload" className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                <Upload size={18} />
                {logoPreview ? 'Change Logo' : 'Upload Logo'}
              </label>
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
                data-testid="logo-upload-input"
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Upload PNG, JPG or SVG. Max size: 2MB
              </p>
            </div>
          </div>
        </div>

        {/* Company Details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Company Name</label>
            <input
              type="text"
              value={companyInfo.name}
              onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
              className="input"
              placeholder="Your Company Name"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Email</label>
            <input
              type="email"
              value={companyInfo.email}
              onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
              className="input"
              placeholder="company@email.com"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Phone</label>
            <input
              type="tel"
              value={companyInfo.phone}
              onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
              className="input"
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Website</label>
            <input
              type="url"
              value={companyInfo.website}
              onChange={(e) => setCompanyInfo({ ...companyInfo, website: e.target.value })}
              className="input"
              placeholder="www.yourcompany.com"
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Address</label>
          <textarea
            value={companyInfo.address}
            onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
            className="input"
            style={{ minHeight: '80px' }}
            placeholder="123 Main Street, City, State ZIP"
          />
        </div>
      </div>

      {/* Company Signature */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <SectionHeader
          icon={Pen}
          tint="#f59e0b"
          title="Company Signature"
          subtitle="Automatically added to all invoices and quotes"
        />

        {!showSignaturePad && (
          <>
            {companySignature ? (
              <div>
                <div style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  background: '#f9fafb',
                  display: 'inline-block'
                }}>
                  <img 
                    src={companySignature} 
                    alt="Company Signature" 
                    style={{ 
                      maxWidth: '300px', 
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => setShowSignaturePad(true)}
                    style={{
                      padding: '0.625rem 1rem',
                      background: '#fff',
                      color: '#0073ea',
                      border: '1px solid #0073ea',
                      borderRadius: '6px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Change Signature
                  </button>
                  <button
                    onClick={removeSignature}
                    style={{
                      padding: '0.625rem 1rem',
                      background: '#fff',
                      color: '#e44258',
                      border: '1px solid #e44258',
                      borderRadius: '6px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Remove Signature
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowSignaturePad(true)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <Pen size={18} />
                Add Company Signature
              </button>
            )}
          </>
        )}

        {showSignaturePad && (
          <div style={{ marginTop: '1rem' }}>
            <SignaturePad
              onSave={handleSaveSignature}
              onCancel={() => setShowSignaturePad(false)}
              initialSignature={companySignature}
              label="Draw Company Signature"
            />
          </div>
        )}
      </div>

      {/* Tax Settings */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <SectionHeader
          icon={Receipt}
          tint="#22c55e"
          title="Tax Settings"
          subtitle="Default sales tax rate applied to new invoices"
        />
        <div style={{ maxWidth: '300px' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Default Sales Tax Rate (%)</label>
          <input
            type="number"
            data-testid="settings-tax-rate-input"
            step="0.001"
            min="0"
            max="100"
            value={taxRate}
            onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
            className="input"
            placeholder="8.00"
          />
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
            This rate will be applied by default to new invoices
          </p>
        </div>
      </div>

      {/* Payment Reminders */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="settings-reminders-card">
        <SectionHeader
          icon={Bell}
          tint="#f97316"
          title="Payment Reminders"
          subtitle="Auto-email customers before their invoices are due"
        />
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.25rem', lineHeight: 1.5 }}>
          Automatically email customers a friendly reminder a few days before their invoice is due.
          You can also click the bell icon on any invoice to send one manually.
        </p>
        <div style={{ display: 'grid', gap: '1rem', maxWidth: '520px' }}>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}
            data-testid="settings-reminders-enabled"
          >
            <input
              type="checkbox"
              checked={reminders.enabled}
              onChange={(e) => setReminders((r) => ({ ...r, enabled: e.target.checked }))}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>Enable auto-reminders</div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                Runs once per day when you open the Invoices tab.
              </div>
            </div>
          </label>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Send reminder this many days before the due date
            </label>
            <input
              type="number"
              data-testid="settings-reminders-days"
              min="0"
              max="30"
              step="1"
              value={reminders.daysBefore}
              onChange={(e) => setReminders((r) => ({ ...r, daysBefore: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
              className="input"
              style={{ maxWidth: '160px' }}
              disabled={!reminders.enabled}
            />
          </div>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', opacity: reminders.enabled ? 1 : 0.5 }}
            data-testid="settings-reminders-overdue"
          >
            <input
              type="checkbox"
              checked={reminders.includeOverdue}
              disabled={!reminders.enabled}
              onChange={(e) => setReminders((r) => ({ ...r, includeOverdue: e.target.checked }))}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>Also remind on past-due invoices</div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                A polite &quot;past due&quot; reminder goes out for any unpaid invoice that has slipped past its due date.
              </div>
            </div>
          </label>

          <div style={{ marginTop: '0.5rem' }}>
            <label
              htmlFor="settings-reminders-message"
              style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.4rem' }}
            >
              Custom reminder message (optional)
            </label>

            {/* Quick-pick presets — click to fill the textarea */}
            <div
              style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}
              data-testid="settings-reminders-presets"
            >
              {[
                {
                  key: 'friendly',
                  label: 'Friendly',
                  text: "Hi {customer}, hope you're doing well! Just a quick heads-up that invoice {invoice} for {amount} is due on {due_date}. Thank you so much for your business — {company} appreciates you!",
                },
                {
                  key: 'professional',
                  label: 'Professional',
                  text: "Dear {customer},\n\nThis is a courtesy reminder that invoice {invoice} in the amount of {amount} is scheduled for payment on {due_date}. Please let us know if you have any questions.\n\nBest regards,\n{company}",
                },
                {
                  key: 'short',
                  label: 'Short & sweet',
                  text: "Hi {customer} — invoice {invoice} ({amount}) is due {due_date}. Thanks!",
                },
                {
                  key: 'firm',
                  label: 'Firm (past due)',
                  text: "Hi {customer}, our records show invoice {invoice} for {amount} was due on {due_date} and remains unpaid. Please arrange payment at your earliest convenience so we can keep your account in good standing. Thank you.",
                },
                {
                  key: 'holiday',
                  label: 'Holiday tone',
                  text: "Hi {customer}, wishing you a wonderful season from all of us at {company}! A friendly reminder that invoice {invoice} for {amount} is due on {due_date}. Thanks again for choosing us this year.",
                },
              ].map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  data-testid={`settings-reminders-preset-${preset.key}`}
                  onClick={() => setReminders((r) => ({ ...r, customMessage: preset.text }))}
                  style={{
                    padding: '0.35rem 0.75rem', borderRadius: 999,
                    border: '1px solid #cbd5e1', background: '#f8fafc',
                    fontSize: 12, fontWeight: 500, color: '#334155',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#e0f2fe'; e.currentTarget.style.borderColor = '#0073ea'; e.currentTarget.style.color = '#0369a1'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#334155'; }}
                >
                  {preset.label}
                </button>
              ))}
              {reminders.customMessage && (
                <button
                  type="button"
                  data-testid="settings-reminders-preset-clear"
                  onClick={() => setReminders((r) => ({ ...r, customMessage: '' }))}
                  style={{
                    padding: '0.35rem 0.75rem', borderRadius: 999,
                    border: '1px solid #fecaca', background: '#fff',
                    fontSize: 12, fontWeight: 500, color: '#dc2626',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >Clear</button>
              )}
            </div>

            <textarea
              id="settings-reminders-message"
              data-testid="settings-reminders-message"
              rows={4}
              value={reminders.customMessage || ''}
              onChange={(e) => setReminders((r) => ({ ...r, customMessage: e.target.value }))}
              className="input"
              placeholder={"Pick a preset above or type your own. Use {customer}, {invoice}, {amount}, {due_date}, {company} to auto-fill each customer's details."}
              style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', minHeight: 90 }}
            />
          </div>
        </div>
      </div>

      {/* Send from My Own Email (per-company SMTP) */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="settings-sender-card">
        <SectionHeader
          icon={Mail}
          tint="#0ea5e9"
          title="Send Reminders From My Own Email"
          subtitle="Required — invoices & reminders send from your own business inbox"
        />
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem', lineHeight: 1.5 }}>
          Every invoice email and payment reminder is sent directly from your own inbox — your customers see your business email as the sender, and their replies land back with you. Configure your sender below to enable sending.
        </p>
        <div style={{
          padding: '0.75rem 1rem', marginBottom: '1rem',
          background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8,
          fontSize: '0.8rem', color: '#78350f', lineHeight: 1.5,
        }}>
          <strong>Gmail users:</strong> Google requires an <em>App Password</em> (not your regular password). Generate one at
          {' '}
          <a
            href="https://myaccount.google.com/apppasswords"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#7c2d12', textDecoration: 'underline', fontWeight: 600 }}
          >
            myaccount.google.com/apppasswords
          </a>
          {' '}(you must have 2-Step Verification turned on first). Paste the 16-character password below.
        </div>

        <div style={{ display: 'grid', gap: '1rem', maxWidth: '560px' }}>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}
            data-testid="settings-sender-enabled"
          >
            <input
              type="checkbox"
              checked={senderSmtp.enabled}
              onChange={(e) => setSenderSmtp((s) => ({ ...s, enabled: e.target.checked }))}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>Use my own email to send reminders</div>
            </div>
          </label>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Sender email address
            </label>
            <input
              type="email"
              data-testid="settings-sender-email"
              value={senderSmtp.email}
              onChange={(e) => setSenderSmtp((s) => ({ ...s, email: e.target.value.trim() }))}
              disabled={!senderSmtp.enabled}
              className="input"
              placeholder="billing@yourcompany.com"
              autoComplete="off"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              App password
            </label>
            <input
              type="password"
              data-testid="settings-sender-password"
              value={senderSmtp.password}
              onChange={(e) => setSenderSmtp((s) => ({ ...s, password: e.target.value }))}
              disabled={!senderSmtp.enabled}
              className="input"
              placeholder="xxxx xxxx xxxx xxxx"
              autoComplete="new-password"
            />
          </div>

          <div style={{ display: showSenderAdvanced ? 'grid' : 'none', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '0.4rem' }}>
                SMTP server
              </label>
              <input
                type="text"
                data-testid="settings-sender-server"
                value={senderSmtp.server}
                onChange={(e) => setSenderSmtp((s) => ({ ...s, server: e.target.value.trim() }))}
                disabled={!senderSmtp.enabled}
                className="input"
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '0.4rem' }}>
                Port
              </label>
              <input
                type="number"
                data-testid="settings-sender-port"
                value={senderSmtp.port}
                onChange={(e) => setSenderSmtp((s) => ({ ...s, port: parseInt(e.target.value, 10) || 587 }))}
                disabled={!senderSmtp.enabled}
                className="input"
                placeholder="587"
              />
            </div>
          </div>

          <button
            type="button"
            data-testid="settings-sender-advanced-toggle"
            onClick={() => setShowSenderAdvanced((v) => !v)}
            style={{
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              color: '#64748b', fontSize: '0.75rem', textDecoration: 'underline',
              textAlign: 'left', marginTop: showSenderAdvanced ? 0 : '-0.25rem',
            }}
          >
            {showSenderAdvanced
              ? 'Hide advanced (SMTP server & port)'
              : 'Advanced: change SMTP server / port (not needed for Gmail)'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="settings-sender-test"
              onClick={testSenderCredentials}
              disabled={!senderSmtp.enabled || senderTestStatus.state === 'testing'}
              style={{
                padding: '0.55rem 1rem', borderRadius: 8, border: '1px solid #0073ea',
                background: senderSmtp.enabled ? '#0073ea' : '#cbd5e1',
                color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: senderSmtp.enabled ? 'pointer' : 'not-allowed',
              }}
            >
              {senderTestStatus.state === 'testing' ? 'Verifying…' : 'Test connection'}
            </button>
            {senderTestStatus.state !== 'idle' && (
              <span
                data-testid="settings-sender-test-result"
                style={{
                  fontSize: '0.8rem',
                  color: senderTestStatus.state === 'ok' ? '#059669'
                    : senderTestStatus.state === 'error' ? '#dc2626' : '#64748b',
                  fontWeight: 500,
                }}
              >
                {senderTestStatus.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Security Section — auto-logout toggle */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="settings-security-card">
        <SectionHeader
          icon={Shield}
          tint="#0ea5e9"
          title="Security"
          subtitle="Session and auto-logout preferences"
        />
        <div style={{ maxWidth: 560 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '1rem',
              padding: '0.9rem 1rem',
              background: security.autoLogout ? '#f1f5f9' : '#fff7ed',
              border: `1px solid ${security.autoLogout ? '#e2e8f0' : '#fed7aa'}`,
              borderRadius: 10,
            }}
            data-testid="settings-auto-logout-row"
          >
            {/* Green pill toggle — matches the Auto-sync toggle on the Storage page. */}
            <button
              type="button"
              onClick={() => setSecurity({ autoLogout: !security.autoLogout })}
              data-testid="settings-auto-logout-toggle"
              aria-label="Toggle auto-logout"
              aria-pressed={security.autoLogout}
              style={{
                position: 'relative',
                width: 44,
                height: 24,
                flexShrink: 0,
                marginTop: 2,
                background: security.autoLogout ? '#16a34a' : '#d1d5db',
                borderRadius: 12,
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s',
                boxShadow: security.autoLogout ? '0 0 0 4px rgba(22,163,74,0.15)' : 'none',
              }}
            >
              <span style={{
                position: 'absolute',
                top: 2,
                left: security.autoLogout ? 22 : 2,
                width: 20,
                height: 20,
                background: '#fff',
                borderRadius: '50%',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a' }}>
                Auto-logout after 5 minutes of inactivity
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
                Recommended for security. Turn this <strong>off</strong> if you use GPS Fleet
                tracking and need the map to stay open during long shifts without being kicked
                out.
              </div>
              {!security.autoLogout && (
                <div style={{
                  fontSize: '0.75rem', color: '#9a3412', marginTop: 8, padding: '6px 10px',
                  background: '#fff7ed', border: '1px dashed #fdba74', borderRadius: 6,
                }}>
                  Auto-logout is disabled. Your session will stay signed in until you log out
                  manually or the refresh token expires.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Send Feedback (owner/admin only) */}
      {(user?.role === 'admin' || user?.role === 'owner') && (
        <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="settings-feedback-card">
          <SectionHeader
            icon={MessageSquare}
            tint="#06b6d4"
            title="Send Feedback"
            subtitle="Report a bug or request a feature — goes straight to the team"
          />
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '1rem', padding: '1rem', background: '#f0fdff',
            border: '1px solid #a5f3fc', borderRadius: 10,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a' }}>
                Have a suggestion or spotted a bug?
              </div>
              <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: 4 }}>
                Include an optional screenshot so we can act faster.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowFeedbackModal(true)}
              data-testid="settings-send-feedback-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '0.6rem 1.1rem', borderRadius: 10,
                background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                color: '#fff', border: 'none', cursor: 'pointer',
                fontSize: '0.9rem', fontWeight: 600,
                boxShadow: '0 4px 14px rgba(8,145,178,0.35)',
                whiteSpace: 'nowrap',
              }}
            >
              <MessageSquare size={16} />
              Send Feedback
            </button>
          </div>
        </div>
      )}

      {/* License Management Section */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <SectionHeader
          icon={Key}
          tint="#8b5cf6"
          title="License Management"
          subtitle="Trial status, license key, and expiration"
        />

        {/* Current License Status */}
        <div style={{
          padding: '1.5rem',
          background: hasLicense && isValid ? '#d3f5e9' : isTrialMode ? '#fff4de' : '#ffdbde',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: `2px solid ${hasLicense && isValid ? '#00854d' : isTrialMode ? '#946800' : '#e44258'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem', marginBottom: '1rem' }}>
            {hasLicense && isValid ? (
              <Shield size={24} color="#00854d" />
            ) : isTrialMode ? (
              <AlertCircle size={24} color="#946800" />
            ) : (
              <AlertCircle size={24} color="#c1003d" />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontWeight: 700, 
                fontSize: '1.1rem',
                color: hasLicense && isValid ? '#00854d' : isTrialMode ? '#946800' : '#c1003d',
                marginBottom: '0.5rem'
              }}>
                {hasLicense && isValid ? 'Active License' : isTrialMode ? 'Trial Mode' : 'No Active License'}
              </div>
              
              {hasLicense && isValid && (
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  <div style={{ marginBottom: '0.25rem' }}>
                    <strong>License Key:</strong> {licenseKey?.replace(/(.{6})(.*)(.{4})/, '$1****$3')}
                  </div>
                  <div style={{ marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Calendar size={14} />
                    <span>Expires: {new Date(expiryDate).toLocaleDateString()}</span>
                  </div>
                  <div style={{ fontWeight: 600, color: licenseDaysRemaining <= 30 ? '#946800' : '#00854d' }}>
                    {licenseDaysRemaining} days remaining
                  </div>
                </div>
              )}

              {isTrialMode && (
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                    {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining in trial
                  </div>
                  <div>Activate a license to unlock full access after trial ends</div>
                </div>
              )}

              {!hasLicense && !isTrialMode && (
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Your trial has expired. Please activate a license to continue using Maidly Business Manager.
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {!hasLicense && (
              <button
                onClick={() => setShowLicenseModal(true)}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <Key size={16} />
                Activate License
              </button>
            )}
            
            {hasLicense && (
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to remove this license? You will need to re-activate it or use trial mode.')) {
                    removeLicense();
                    toast.success('License removed successfully');
                  }
                }}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: '#fff',
                  color: '#e44258',
                  border: '1px solid #e44258',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Remove License
              </button>
            )}
          </div>
        </div>

        {/* License Information */}
        <div style={{ fontSize: '0.875rem', color: '#6b7280', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>About Licenses:</div>
          <ul style={{ marginLeft: '1.25rem', marginTop: '0.5rem' }}>
            <li>Free 15-day trial with full features</li>
            <li>One license per device/installation</li>
            <li>License valid for 1 year from activation</li>
            <li>Works offline after initial activation</li>
            <li>Contact support for license transfer</li>
          </ul>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={saveSettings}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Save size={18} />
          Save All Settings
        </button>
      </div>

      {/* License Modal */}
      <LicenseModal 
        isOpen={showLicenseModal}
        onClose={() => setShowLicenseModal(false)}
        canClose={true}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
      </div>
      )}
    </div>
  );
};

export default Settings;
